import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ArrowRight } from "lucide-react";
import TiltCard from "./TiltCard";
import { ToolIcon } from "../lib/toolIcons";
import { tools } from "../data/tools";

const DailyInnovations = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });

  return (
    <section id="innovations" className="section-padding bg-dark-200 relative overflow-hidden" aria-labelledby="innovations-heading">
      <div className="absolute inset-0 bg-mesh-gradient opacity-40 pointer-events-none" />

      <div className="section-container relative z-10">
        <motion.div
          ref={ref}
          className="text-center mb-14"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="text-accent-cyan font-semibold text-sm uppercase tracking-widest">
            Free Online Tools
          </span>
          <h2 id="innovations-heading" className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5 gradient-text">
            Daily Innovations
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Free utilities updated regularly — video downloaders, format converters,
            and more. Use them instantly, no signup required.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {tools.map((tool, index) => (
            <motion.div
              key={tool.slug}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.08 + index * 0.06 }}
            >
              <TiltCard glowColor={tool.color} className="h-full">
                <Link
                  to={`/tools/${tool.slug}`}
                  className={`block h-full glass-panel-hover p-6 bg-gradient-to-br ${tool.gradient} group`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-primary-400 flex-shrink-0 mt-0.5">
                      <ToolIcon slug={tool.slug} className="w-7 h-7" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-bold text-white mb-2 group-hover:text-primary-300 transition-colors">
                        {tool.title}
                      </h3>
                      <p className="text-sm text-gray-400 leading-relaxed mb-4">
                        {tool.description}
                      </p>
                      <span className="inline-flex items-center gap-1 text-sm text-primary-400 font-medium group-hover:gap-2 transition-all">
                        Use Tool
                        <ArrowRight className="w-4 h-4" strokeWidth={1.75} aria-hidden />
                      </span>
                    </div>
                  </div>
                </Link>
              </TiltCard>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
        >
          <Link
            to="/tools"
            className="btn-outline inline-block text-base"
          >
            Browse All Free Online Tools
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default DailyInnovations;
